import { useState, useEffect } from 'react';
import { Sparkles, Check, Lock, ArrowRight, Loader2, AlertCircle, X, CreditCard } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * UnlockProposalModal - Payment modal for "Sblocco Proposta Curata"
 * 
 * Single product: €9.90 one-time payment
 * Unlocks: Provider names, concrete draft, operational map
 */
export const UnlockProposalModal = ({ 
  isOpen, 
  onClose, 
  threadId, 
  locale = 'it',
  onUnlockSuccess,
}) => {
  const [status, setStatus] = useState('idle'); // idle, loading, redirecting, error
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  
  const isIT = locale === 'it';

  // Load product info on mount
  useEffect(() => {
    if (isOpen) {
      loadProduct();
    }
  }, [isOpen]);

  const loadProduct = async () => {
    try {
      const res = await axios.get(`${API}/payments/products`);
      const products = res.data.products;
      if (products && products.length > 0) {
        setProduct(products[0]); // proposal_unlock is the only product
      }
    } catch (err) {
      console.error('Failed to load product:', err);
    }
  };

  const handleUnlock = async () => {
    setStatus('loading');
    setError(null);
    
    try {
      // Get origin URL for redirects
      const originUrl = window.location.origin;
      
      const res = await axios.post(`${API}/payments/checkout`, {
        thread_id: threadId,
        product_type: 'proposal_unlock',
        origin_url: originUrl,
      });
      
      if (res.data.already_unlocked) {
        // Already unlocked - notify and close
        if (onUnlockSuccess) {
          onUnlockSuccess();
        }
        onClose();
        return;
      }
      
      // Redirect to Stripe Checkout
      if (res.data.checkout_url) {
        setStatus('redirecting');
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (err) {
      console.error('Checkout error:', err);
      setStatus('error');
      setError(isIT 
        ? 'Si è verificato un errore. Riprova tra qualche istante.'
        : 'An error occurred. Please try again in a moment.'
      );
    }
  };

  if (!isOpen) return null;

  const benefits = isIT ? [
    'Provider reali con nomi e contatti',
    'Bozza di viaggio concreta e dettagliata',
    'Mappa operativa con tappe precise',
    'Link per riprendere quando vuoi',
    'Assistenza via email',
  ] : [
    'Real providers with names and contacts',
    'Concrete and detailed travel draft',
    'Operational map with precise stops',
    'Link to resume whenever you want',
    'Email support',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="bg-gradient-to-br from-umbria-olive to-umbria-sage p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 rounded-full hover:bg-white/20 transition-colors"
          >
            <X size={20} />
          </button>
          
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Sparkles size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold">
                {isIT ? 'Sblocca la Proposta' : 'Unlock Proposal'}
              </h2>
              <p className="text-white/80 text-sm">
                {isIT ? 'Un passo avanti verso il tuo viaggio' : 'One step closer to your trip'}
              </p>
            </div>
          </div>
          
          {/* Price */}
          <div className="flex items-baseline gap-1 mt-4">
            <span className="text-4xl font-bold">€{product?.amount || '9,90'}</span>
            <span className="text-white/70">
              {isIT ? 'una tantum' : 'one-time'}
            </span>
          </div>
        </div>
        
        {/* Benefits */}
        <div className="p-6">
          <p className="text-gray-600 mb-4">
            {isIT 
              ? 'Con lo sblocco ottieni subito:'
              : 'With unlock you get immediately:'
            }
          </p>
          
          <ul className="space-y-3 mb-6">
            {benefits.map((benefit, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-umbria-olive/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={12} className="text-umbria-olive" />
                </div>
                <span className="text-gray-700">{benefit}</span>
              </li>
            ))}
          </ul>
          
          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          {/* CTA Button */}
          <button
            onClick={handleUnlock}
            disabled={status === 'loading' || status === 'redirecting'}
            className="w-full py-4 px-6 bg-gradient-to-r from-umbria-olive to-umbria-sage text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            data-testid="unlock-proposal-btn"
          >
            {status === 'loading' || status === 'redirecting' ? (
              <>
                <Loader2 size={22} className="animate-spin" />
                {isIT ? 'Reindirizzamento...' : 'Redirecting...'}
              </>
            ) : (
              <>
                <CreditCard size={22} />
                {isIT ? 'Sblocca ora' : 'Unlock now'}
                <ArrowRight size={20} />
              </>
            )}
          </button>
          
          {/* Security note */}
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-400">
            <Lock size={12} />
            <span>
              {isIT 
                ? 'Pagamento sicuro via Stripe'
                : 'Secure payment via Stripe'
              }
            </span>
          </div>
          
          {/* Skip option */}
          <button
            onClick={onClose}
            className="w-full mt-3 py-2 text-gray-500 text-sm hover:text-gray-700 transition-colors"
          >
            {isIT ? 'Non ora, continuo a esplorare' : 'Not now, continue exploring'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * PaymentSuccessHandler - Handles return from Stripe Checkout
 * 
 * Polls payment status and updates UI accordingly.
 * Shows clear success/error states with appropriate messaging.
 */
export const PaymentSuccessHandler = ({ 
  sessionId, 
  onSuccess, 
  onError,
  locale = 'it',
}) => {
  const [status, setStatus] = useState('checking'); // checking, success, error, cancelled
  const [attempts, setAttempts] = useState(0);
  const [paymentData, setPaymentData] = useState(null);
  
  const isIT = locale === 'it';
  const maxAttempts = 10;  // Increased for webhook latency
  const pollInterval = 1500;  // Faster initial polls

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      if (onError) {
        onError(isIT 
          ? 'Verifica pagamento in corso. Aggiorna la pagina tra qualche secondo.'
          : 'Payment verification in progress. Refresh the page in a few seconds.'
        );
      }
      return;
    }

    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = res.data;
      
      if (data.status === 'completed' || data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(data);
        if (onSuccess) {
          onSuccess(data);
        }
        return;
      }
      
      if (data.status === 'expired') {
        setStatus('expired');
        if (onError) {
          onError(isIT
            ? 'Sessione di pagamento scaduta. Riprova.'
            : 'Payment session expired. Please try again.'
          );
        }
        return;
      }
      
      if (data.status === 'failed') {
        setStatus('failed');
        if (onError) {
          onError(isIT
            ? 'Pagamento non riuscito. Riprova o contattaci.'
            : 'Payment failed. Please try again or contact us.'
          );
        }
        return;
      }

      if (data.status === 'cancelled') {
        setStatus('cancelled');
        // Don't call onError for cancellation - just redirect back
        window.history.replaceState({}, document.title, window.location.pathname);
        window.location.reload();
        return;
      }
      
      // Still pending - continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
      
    } catch (err) {
      console.error('Payment status check error:', err);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  if (status === 'checking') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm">
        <div className="text-center max-w-sm mx-auto p-8">
          <Loader2 size={56} className="mx-auto text-umbria-olive animate-spin mb-6" />
          <h2 className="text-xl font-semibold text-umbria-charcoal mb-2">
            {isIT ? 'Verifica pagamento in corso...' : 'Verifying payment...'}
          </h2>
          <p className="text-gray-500 text-sm">
            {isIT ? 'Un momento, stiamo confermando il tuo acquisto' : 'Just a moment, confirming your purchase'}
          </p>
          <div className="mt-4 flex justify-center gap-1">
            {[...Array(3)].map((_, i) => (
              <div 
                key={i} 
                className="w-2 h-2 rounded-full bg-umbria-olive/40 animate-pulse"
                style={{ animationDelay: `${i * 200}ms` }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6 animate-bounce-once">
            <Check size={48} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-umbria-charcoal mb-3">
            {isIT ? '🎉 Proposta Sbloccata!' : '🎉 Proposal Unlocked!'}
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            {isIT 
              ? 'Ora hai accesso ai nomi delle strutture, alla bozza dettagliata e alla mappa operativa. Chiara è pronta a darti tutti i dettagli!'
              : 'You now have access to real provider names, detailed draft, and operational map. Chiara is ready to give you all the details!'
            }
          </p>
          
          {/* Value highlight */}
          <div className="bg-umbria-sage/10 rounded-xl p-4 mb-6 text-left">
            <p className="text-sm font-medium text-umbria-olive mb-2">
              {isIT ? 'Cosa hai sbloccato:' : 'What you unlocked:'}
            </p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li className="flex items-center gap-2">
                <Check size={14} className="text-green-500 flex-shrink-0" />
                {isIT ? 'Nomi reali di hotel e agriturismi' : 'Real hotel and agriturismo names'}
              </li>
              <li className="flex items-center gap-2">
                <Check size={14} className="text-green-500 flex-shrink-0" />
                {isIT ? 'Itinerario dettagliato giorno per giorno' : 'Detailed day-by-day itinerary'}
              </li>
              <li className="flex items-center gap-2">
                <Check size={14} className="text-green-500 flex-shrink-0" />
                {isIT ? 'Link per riprendere quando vuoi' : 'Link to resume anytime'}
              </li>
            </ul>
          </div>
          
          <button
            onClick={() => {
              window.history.replaceState({}, document.title, window.location.pathname);
              window.location.reload();
            }}
            className="px-10 py-4 bg-umbria-olive text-white rounded-xl font-semibold hover:bg-umbria-olive/90 transition-all hover:scale-105 shadow-lg"
            data-testid="continue-after-payment-btn"
          >
            {isIT ? 'Continua con Chiara →' : 'Continue with Chiara →'}
          </button>
        </div>
      </div>
    );
  }

  if (status === 'timeout') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
            <AlertCircle size={40} className="text-yellow-600" />
          </div>
          <h2 className="text-xl font-semibold text-umbria-charcoal mb-3">
            {isIT ? 'Verifica in corso' : 'Verification in progress'}
          </h2>
          <p className="text-gray-600 mb-6">
            {isIT 
              ? 'Il pagamento è in elaborazione. Aggiorna la pagina o controlla la tua email per conferma.'
              : 'Payment is being processed. Refresh the page or check your email for confirmation.'
            }
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-8 py-3 bg-umbria-olive text-white rounded-xl font-semibold hover:bg-umbria-olive/90 transition-colors"
          >
            {isIT ? 'Aggiorna pagina' : 'Refresh page'}
          </button>
        </div>
      </div>
    );
  }

  // Error/failed states
  if (status === 'failed' || status === 'expired') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <X size={40} className="text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-umbria-charcoal mb-3">
            {isIT ? 'Pagamento non completato' : 'Payment not completed'}
          </h2>
          <p className="text-gray-600 mb-6">
            {status === 'expired' 
              ? (isIT ? 'La sessione di pagamento è scaduta.' : 'Payment session expired.')
              : (isIT ? 'Il pagamento non è andato a buon fine.' : 'Payment was not successful.')
            }
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                window.history.replaceState({}, document.title, window.location.pathname);
                window.location.reload();
              }}
              className="px-6 py-3 bg-umbria-olive text-white rounded-xl font-semibold hover:bg-umbria-olive/90 transition-colors"
            >
              {isIT ? 'Riprova' : 'Try again'}
            </button>
            <button
              onClick={() => {
                window.history.replaceState({}, document.title, window.location.pathname);
                window.location.reload();
              }}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
            >
              {isIT ? 'Torna alla chat' : 'Back to chat'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default UnlockProposalModal;
